import type {
  CapabilityReadinessAction,
  CapabilityReadinessItem,
} from "./capabilityReadinessSchemas";

const blockingLabels: Record<string, string> = {
  prerequisite_missing_deposit: "TikTok Shop 保证金尚未完成",
  prerequisite_missing_gmail_connection: "需要连接 Gmail 后才能使用邮件能力",
  prerequisite_missing_logistics: "TikTok Shop 物流配置尚未完成",
  prerequisite_missing_products: "TikTok Shop 暂无可用商品",
  prerequisite_missing_return_warehouse: "TikTok Shop 退货仓尚未完成",
  prerequisite_missing_shipping_warehouse: "TikTok Shop 发货仓尚未完成",
  prerequisite_missing_tax_identity: "TikTok Shop 税务身份尚未完成",
  shop_reauthorization_required: "店铺授权需要恢复",
  shop_sync_degraded: "店铺状态暂时无法完整确认",
  source_binding_inactive: "店铺连接已失效",
};

function needsCapabilityAttention(item: CapabilityReadinessItem) {
  return (
    item.state === "action_required" ||
    item.state === "degraded" ||
    item.state === "unavailable" ||
    (item.state === "unknown" && item.actions.length > 0)
  );
}

export function selectCapabilityReadinessNoticeItem(
  items: readonly CapabilityReadinessItem[],
  shopBindingId = "",
) {
  if (shopBindingId) {
    const shopItem = items.find(
      (item) =>
        item.shop_binding_id === shopBindingId && needsCapabilityAttention(item),
    );
    if (shopItem) return shopItem;
  }
  return items.find(needsCapabilityAttention);
}

export function capabilityReadinessMessage(item: CapabilityReadinessItem) {
  if (item.blocking_codes.length === 0) {
    return item.state === "degraded"
      ? "能力状态暂时无法完整确认"
      : item.state === "unknown"
        ? "能力前置状态尚未确认"
        : "";
  }
  return item.blocking_codes
    .map((code) => blockingLabels[code] ?? publicCodeLabel(code))
    .join("；");
}

export function capabilityReadinessActionPath(
  action: CapabilityReadinessAction,
  input: { returnTo: string; shopBindingId: string },
) {
  switch (action) {
    case "connect_gmail":
      return `/settings/connections?provider=gmail&returnTo=${encodeURIComponent(safeReturnTo(input.returnTo))}`;
    case "open_shop_prerequisite":
      return `/settings/stores${input.shopBindingId ? `?shop_binding_id=${encodeURIComponent(input.shopBindingId)}&focus=prerequisites` : ""}`;
    case "open_products":
      return null;
    case "retry_probe":
      return null;
  }
}

export function safeReturnTo(value: string) {
  const trimmed = value.trim();
  if (
    !trimmed.startsWith("/") ||
    trimmed.startsWith("//") ||
    trimmed.includes("\\")
  )
    return "/workspace";
  try {
    const parsed = new URL(trimmed, "https://merry.invalid");
    if (
      parsed.origin !== "https://merry.invalid" ||
      !parsed.pathname.startsWith("/workspace")
    )
      return "/workspace";
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return "/workspace";
  }
}

function publicCodeLabel(code: string) {
  return code.replaceAll("_", " ");
}
